import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Table } from '@tanstack/react-table'

import { InputFormValues, InputFormValuesEdit } from '../types'
import { useCreateControllerLog } from '../api/useCreateControllerLog'
import { useUpdateControllerLog } from '../api/useUpdateControllerLog'
import { useCombinedLogsTableFormStateStore } from '../store'
import { Log } from '@/types/logs'
import { useClickOutside } from '@/helpers/useClickOutside'
import { useFormSync } from './useFormSync'
import { useUpdateSharedLogs } from '../api/useUpdateSharedLogs'
import { useNotification } from '@/ui/Notification'

export const useCombinedLogsForm = ({
    table,
    setRowSelection,
    isShared = false,
}: {
    table: Table<Log>
    setRowSelection: (rowSelection: Record<string, boolean>) => void
    isShared?: boolean
}) => {
    const { handleCreateControllerLog } = useCreateControllerLog()
    const { handleUpdateControllerLog } = useUpdateControllerLog()
    const { handleUpdateSharedLogs } = useUpdateSharedLogs()
    const { clearInputValues } = useCombinedLogsTableFormStateStore()
    const [isCreateFormActive, setIsCreateFormActive] = useState(false)
    const { showNotification } = useNotification()

    const { selectedRowId } = useCombinedLogsTableFormStateStore()

    const form = useForm<InputFormValuesEdit>({
        defaultValues: {
            timeStamp: '',
            code: '',
            rtnTrips: '',
            mileage: '',
            isZeroMileage: 'false',
            author: '',
            locationFrom: '',
            description: '',
        },
    })
    const { isDirty } = form.formState

    const onSubmit = (data: InputFormValues) => {
        if (isCreateFormActive) {
            form.reset()
            handleCreateControllerLog(data)
            clearInputValues()
        } else if (selectedRowId && Object.keys(table.getState().rowSelection).length !== 0) {
            try {
                if (isDirty) {
                    if (isShared) {
                        handleUpdateSharedLogs(data, selectedRowId, form.formState.defaultValues?.timeStamp)
                    } else {
                        handleUpdateControllerLog(data, selectedRowId, form.formState.defaultValues?.timeStamp)
                    }
                }
            } catch (error) {
                showNotification({
                    type: 'error',
                    title: 'Changes not saved',
                    message:
                        "We're sorry, something unexpected has happened. Please check your work, as not all changes may have been saved.",
                    manualDismiss: true,
                })

                // Log to application insights too
                throw error
            }
            form.reset()
            setRowSelection({})
            clearInputValues()
        }
    }

    const handleClickAwayFromEditForm = () => {
        if (Object.keys(table.getState().rowSelection).length !== 0) {
            form.handleSubmit(onSubmit)()
        }
    }

    const editWrapperRef = useClickOutside<HTMLTableRowElement>(handleClickAwayFromEditForm, true)

    useFormSync({ form })

    return {
        form,
        onSubmit,
        handleClickAwayFromEditForm,
        isCreateFormActive,
        setIsCreateFormActive,
        editWrapperRef,
    }
}
